import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import HeroCarousel from "@/components/feature/HeroCarousel";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import SearchBox from "@/components/feature/SearchBox";
import { movies } from "@/mocks/movies";
import { sortMovies } from "@/utils/movieUtils";
import { usePageMeta } from "@/hooks/usePageMeta";

const CATEGORY_CARDS = [
  {
    type: "电影",
    title: "电影专区",
    desc: "剧情、悬疑、爱情、喜剧，一站式浏览院线佳作的剧情介绍。",
    count: "1200+ 部",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20collage%20of%20film%20reels%20and%20dark%20red%20theater%20seats%2C%20moody%20deep%20crimson%20and%20gold%20tones%2C%20dramatic%20lighting%2C%20dark%20background%2C%20no%20text&width=800&height=500&seq=ymdh-cat-movie&orientation=landscape",
  },
  {
    type: "电视剧",
    title: "电视剧专区",
    desc: "都市、情感、悬疑、治愈，追更热播剧集与经典系列。",
    count: "860+ 部",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20living%20room%20with%20a%20glowing%20television%20screen%20in%20warm%20red%20light%20at%20night%2C%20moody%20deep%20crimson%20and%20gold%20tones%2C%20dramatic%2C%20dark%20background%2C%20no%20text&width=800&height=500&seq=ymdh-cat-tv&orientation=landscape",
  },
];

export default function Home() {
  usePageMeta({
    title: "岳母的诱惑 - 电影电视剧在线观看与剧情介绍",
    description:
      "岳母的诱惑为您提供最新影视内容，包括电影和电视剧的在线观看与详细剧情介绍，让您轻松了解影视精彩内容。",
    keywords: "岳母的诱惑,电影,电视剧,影视在线观看,剧情介绍,影视内容,最新影视",
  });

  const featured = sortMovies(movies, "heat").slice(0, 5);
  const latest = sortMovies(movies, "update").slice(0, 10);
  const popular = sortMovies(movies, "heat").slice(0, 8);

  return (
    <Layout overlayNav>
      <HeroCarousel items={featured} />

      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="分类导航"
          title="按类型快速进入"
          description="从电影与电视剧两大内容库出发，快速定位你感兴趣的影视作品。"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {CATEGORY_CARDS.map((card) => (
            <Link
              key={card.type}
              to={`/category?type=${encodeURIComponent(card.type)}`}
              className="group relative block h-56 md:h-64 rounded-lg overflow-hidden border border-background-800 hover:border-primary-600 transition-colors"
            >
              <img
                src={card.image}
                alt={`${card.title}入口`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-transparent" />
              <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end">
                <span className="inline-flex w-fit items-center gap-1 px-2.5 py-0.5 rounded-md text-xs bg-accent-500 text-foreground-950 font-medium mb-2">
                  {card.count}
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                  {card.title}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-300 max-w-md">
                  {card.desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm text-accent-400 group-hover:gap-2 transition-all">
                  进入分类
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-900/40 border-y border-background-800">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <SectionHeading
            eyebrow="最新更新"
            title="最新更新影视"
            description="持续收录近期上线与完结的影视作品，随时了解最新剧情动态。"
            action={{ label: "查看全部", to: "/category" }}
          />
          <MovieGrid items={latest} />
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="热榜推荐"
          title="热门影视推荐"
          description="根据站内浏览与观看热度综合排序，看看大家都在追什么。"
          action={{ label: "更多推荐", to: "/category" }}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {popular.map((movie, index) => (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group flex items-center gap-4 p-3 rounded-lg bg-background-900 border border-background-800 hover:border-primary-600 transition-colors"
            >
              <span
                className={`w-8 text-center font-heading font-black text-lg shrink-0 ${
                  index < 3 ? "text-accent-500" : "text-foreground-600"
                }`}
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="w-14 h-20 rounded-md overflow-hidden bg-background-800 shrink-0">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm md:text-base font-medium text-foreground-100 truncate group-hover:text-accent-400 transition-colors">
                  {movie.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500 truncate">
                  {movie.genres.join(" · ")} · {movie.region} · {movie.year}
                </p>
                <p className="mt-1 text-xs text-foreground-600">
                  评分 {movie.rating} · 热度 {(movie.heat / 1000).toFixed(1)}k
                </p>
              </div>
              <i className="ri-arrow-right-s-line text-xl text-foreground-600 group-hover:text-accent-400 transition-colors"></i>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-900 border-t border-background-800">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-400 mb-3">
                <span className="w-4 h-px bg-accent-500"></span>
                关于岳母的诱惑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 leading-snug text-balance">
                让每一段剧情，都值得细细品读
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-300 leading-relaxed">
                「岳母的诱惑」是一个专注于电影与电视剧内容的影视门户，为影视爱好者整理作品简介、剧情脉络、类型与地区等关键信息。我们希望用清晰的信息层级和真实的剧情内容，帮助你快速找到想看的作品。
              </p>
              <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
                {[
                  { value: "2060+", label: "收录作品" },
                  { value: "48", label: "内容分类" },
                  { value: "每日", label: "持续更新" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center lg:text-left">
                    <div className="font-heading text-2xl font-black text-accent-500">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-xs text-foreground-500">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-7 max-w-md">
                <SearchBox />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-calendar-check-line", title: "更新及时", desc: "紧跟影视更新节奏，第一时间同步最新内容与剧情。" },
                { icon: "ri-filter-3-line", title: "精细筛选", desc: "按类型、地区、年份多维筛选，快速缩小范围。" },
                { icon: "ri-heart-3-line", title: "本地收藏", desc: "一键收藏想看的作品，收藏记录保存在本机。" },
                { icon: "ri-shield-check-line", title: "剧情清晰", desc: "详尽的剧情介绍与相关信息，观影前更了解作品。" },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-4 md:p-5 rounded-lg bg-background-950 border border-background-800"
                >
                  <span className="w-10 h-10 rounded-md bg-primary-950 text-accent-400 flex items-center justify-center">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 text-sm md:text-base font-medium text-foreground-100">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}